import * as React from "react";
import { AssistantAdapter, Citation, EditInfo } from "../../../platform";
import MessageMarkdown from "./MessageMarkdown";
import CitationSources from "./CitationSources";
import EditCardList from "./EditCardList";
import MessageMeta from "./MessageMeta";
import WorkingCard from "./WorkingCard";
import { StatusStep } from "../types/status-step";
import { stripCitationMarkers } from "../utils/citationParser";

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  edits?: EditInfo[];
  citations?: Citation[];
  model?: string;
  timestamp?: number;
  /** What the assistant did to produce this reply, kept so the turn can be inspected afterwards. */
  steps?: StatusStep[];
  /** How long the turn took, from send to reply, for the Worked card. */
  durationMs?: number;
}

interface ChatMessageBubbleProps {
  message: ChatMessage;
  messageIndex: number;
  assistant: AssistantAdapter;
  /** The edit the document is currently showing, when it belongs to this message. */
  activeEditIndex?: number | null;
  onJumpToEdit: (messageIndex: number, editIndex: number) => void;
  onAcceptEdit: (messageIndex: number, editIndex: number) => void;
  onRejectEdit: (messageIndex: number, editIndex: number) => void;
  onAcceptAll: (messageIndex: number) => void;
  onRejectAll: (messageIndex: number) => void;
}

/**
 * One turn in the chat.
 *
 * The two roles are drawn differently on purpose. The user's words sit in a tinted
 * bubble on the right, because that is the convention every chat client has taught.
 * The assistant's reply is not in a bubble at all: it is the pane's own voice, so it
 * runs full width. It used to sit under an identity row -- the Jylo mark, "Jylo", the
 * model and the time -- but in a pane that only ever has one assistant, naming it on
 * every turn said nothing, and the detail it carried now lives in the footer both roles
 * share (MessageMeta): model, time and Copy, shown on hover, as in the web client.
 */
const ChatMessageBubble: React.FC<ChatMessageBubbleProps> = ({
  message,
  messageIndex,
  assistant,
  activeEditIndex,
  onJumpToEdit,
  onAcceptEdit,
  onRejectEdit,
  onAcceptAll,
  onRejectAll,
}) => {
  // A host without `reveal` cannot move to anything inside the open item -- Outlook,
  // whose Mailbox API has no call that scrolls or highlights. It is handed no citations
  // at all (DraftChat strips the tags), so the footer below simply never renders; what
  // this still governs is the edit cards' jump button.
  const canNavigate = assistant.reveal !== undefined;
  const navigate = React.useCallback(
    (target: Citation) => void assistant.reveal?.(target),
    [assistant]
  );

  if (message.role === "user") {
    return (
      <div className="group flex flex-col items-end gap-0.5">
        <div className="max-w-[86%] rounded-[12px_12px_4px_12px] bg-primary-tint px-3 py-2 text-body1 whitespace-pre-wrap text-strong [word-break:break-word]">
          {message.content}
        </div>
        <MessageMeta timestamp={message.timestamp} copyText={message.content} align="end" />
      </div>
    );
  }

  // Word (its revisions) and Outlook (its own write history) can accept or reject an
  // individual edit after the fact. Elsewhere the cards still list what changed -- that
  // is useful, and with no undo it is the only record -- but offering buttons that
  // cannot do anything would be worse than offering none.
  const canReview = assistant.review !== undefined;
  const steps = message.steps ?? [];

  return (
    <div className="group flex min-w-0 flex-col gap-2.5">
      {steps.length > 0 && (
        <WorkingCard steps={steps} live={false} durationMs={message.durationMs} />
      )}

      <MessageMarkdown
        content={message.content}
        citations={message.citations}
        onNavigate={navigate}
      />

      {message.edits && message.edits.length > 0 && (
        <EditCardList
          edits={message.edits}
          messageIndex={messageIndex}
          activeEditIndex={activeEditIndex}
          canReview={canReview}
          canNavigate={canNavigate}
          onJumpToEdit={onJumpToEdit}
          onAcceptEdit={onAcceptEdit}
          onRejectEdit={onRejectEdit}
          onAcceptAll={onAcceptAll}
          onRejectAll={onRejectAll}
        />
      )}

      {message.citations && message.citations.length > 0 && (
        <CitationSources citations={message.citations} onNavigate={navigate} />
      )}

      <div className="-mt-1.5 -ml-1">
        <MessageMeta
          model={message.model}
          timestamp={message.timestamp}
          copyText={stripCitationMarkers(message.content)}
        />
      </div>
    </div>
  );
};

export default ChatMessageBubble;
